import { Component, Inject, Input, OnInit, QueryList, ViewChildren, OnDestroy } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { SelectCountryCodeModalComponent } from '../select-country-code-modal/select-country-code-modal.component';
import Env from '@ridango/core/env';
import { PhoneNumberUtil } from 'google-libphonenumber';

import {
    CorporateEmployeesService,
    SmsResponseStatus
} from '@ridango/components/lib/corporate-employees/corporate-employees.service';
import { NotificationFacade } from '@ridango/components';
import { takeUntil } from 'rxjs/operators';
import { BaseComponent } from '@ridango/core';
import { TranslateService } from '@ngx-translate/core';
enum ModalStep {
    phoneNumber,
    code
}

enum ErrorType {
    incorrectNumber = 'PHONE_NUMBER_INCORRECT_MESSAGE',
    incorrectCode = 'INVALID_SMS_CODE',
    commonErrorMessage = 'SMS_VERIFICATION_COMMON_ERROR_MESSAGE',
    verifyingSmsCodeError = 'VERIFYING_SMS_CODE_ERROR',
    codeNotProvided = 'COUNTRY_CALLING_CODE_NOT_PROVIDED',
    regionNotFound = 'REGION_NOT_FOUND_ERROR',
    sendingSmsCodeError = 'SENDING_SMS_CODE_ERROR',
    maxAttemptsReached = 'MAX_ATTEMPTS_FOR_THE_GIVEN_PHONE_NUMBER_REACHED',
    notFound = 'CORPORATE_EMPLOYEE_NOT_FOUND'
}

const phoneNumberUtil = PhoneNumberUtil.getInstance();

@Component({
    selector: 'gl-add-company-modal',
    templateUrl: './add-company-modal.component.html',
    styleUrls: ['./add-company-modal.component.scss']
})
export class AddCompanyModalComponent extends BaseComponent implements OnInit, OnDestroy {
    selectedModalStep = ModalStep.phoneNumber;
    ModalStep = ModalStep;
    selectedCode: string;
    phoneNumber = '';
    errorType = null;
    enteredCode = ['', '', '', '', '', ''];
    isRequestSending = false;
    @ViewChildren('codeInputs') inputs: QueryList<any>;
    @Input() availableCountryCallingCodes = [];

    constructor(
        private modalCtrl: ModalController,
        @Inject('ENV') public env: Env,
        private corporateEmployeesService: CorporateEmployeesService,
        private notification: NotificationFacade,
        private translate: TranslateService
    ) {
        super();
    }

    ngOnInit(): void {
        this.selectedCode = this.env?.DEFAULT_PHONE_COUNTRY_CODE ?? this.availableCountryCallingCodes?.[0] ?? '';
    }

    handleBackClicked() {
        this.selectedModalStep = ModalStep.phoneNumber;
    }

    closeModal() {
        this.modalCtrl.dismiss(null, 'cancel');
    }

    handleNextClick() {
        if (this.selectedModalStep === ModalStep.phoneNumber) {
            const phoneNumber = phoneNumberUtil.parseAndKeepRawInput(this.selectedCode + this.phoneNumber);

            if (this.phoneNumber && phoneNumberUtil.isValidNumber(phoneNumber)) {
                this.isRequestSending = true;
                this.corporateEmployeesService
                    .sendSms(this.selectedCode, this.phoneNumber)
                    .pipe(takeUntil(this.componentDestroyed$))
                    .subscribe(
                        (request) => {
                            this.isRequestSending = false;
                            if (request.status !== SmsResponseStatus.CANCELED) {
                                this.selectedModalStep = ModalStep.code;
                                this.errorType = null;

                                setTimeout(() => {
                                    this.inputs.toArray()[0]?.setFocus();
                                });
                            } else {
                                this.errorType = ErrorType.commonErrorMessage;
                            }
                        },
                        (error) => {
                            this.isRequestSending = false;

                            switch (error.error) {
                                case ErrorType.maxAttemptsReached:
                                case ErrorType.codeNotProvided:
                                case ErrorType.regionNotFound:
                                case ErrorType.notFound:
                                case ErrorType.sendingSmsCodeError:
                                    this.errorType = error.error;
                                    break;
                                default:
                                    this.errorType = ErrorType.commonErrorMessage;
                            }
                        }
                    );
            } else {
                this.errorType = ErrorType.incorrectNumber;
            }
        } else {
            this.isRequestSending = true;
            this.corporateEmployeesService
                .checkSms(this.selectedCode, this.phoneNumber, this.enteredCode.join(''))
                .pipe(takeUntil(this.componentDestroyed$))
                .subscribe(
                    (request) => {
                        this.isRequestSending = false;

                        if (request.status === SmsResponseStatus.APPROVED) {
                            this.notification.success(this.translate.instant('COMPANY_HAS_BEEN_ADDED'), {
                                showAfterRouteChange: false,
                                duration: 3000
                            });

                            this.modalCtrl.dismiss(true, 'cancel');
                        } else {
                            this.errorType = ErrorType.incorrectCode;
                        }
                    },
                    (error) => {
                        this.isRequestSending = false;

                        switch (error.error) {
                            case ErrorType.incorrectCode:
                            case ErrorType.verifyingSmsCodeError:
                            case ErrorType.maxAttemptsReached:
                            case ErrorType.notFound:
                            case ErrorType.codeNotProvided:
                            case ErrorType.regionNotFound:
                                this.errorType = error.error;

                                break;
                            default:
                                this.errorType = ErrorType.commonErrorMessage;
                        }
                    }
                );
        }
    }

    numberChange(event) {
        this.errorType = null;
        this.phoneNumber = event.target.value;
    }

    handleResendPasswordClicked() {
        this.errorType = null;

        this.isRequestSending = true;
        this.corporateEmployeesService
            .sendSms(this.selectedCode, this.phoneNumber)
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe(
                () => {
                    this.isRequestSending = false;
                    this.enteredCode = ['', '', '', '', '', ''];
                    this.focusInput(0);
                },
                (error) => {
                    this.isRequestSending = false;

                    switch (error.error) {
                        case ErrorType.maxAttemptsReached:
                        case ErrorType.codeNotProvided:
                        case ErrorType.regionNotFound:
                        case ErrorType.notFound:
                        case ErrorType.sendingSmsCodeError:
                            this.errorType = error.error;
                            break;
                        default:
                            this.errorType = ErrorType.commonErrorMessage;
                    }
                }
            );
    }

    async handleSelectCountryClick() {
        const modal = await this.modalCtrl.create({
            component: SelectCountryCodeModalComponent,
            cssClass: 'select-country-modal',
            componentProps: {
                availableCountryCallingCodes: this.availableCountryCallingCodes,
                selectedCountryCode: this.selectedCode
            }
        });

        await modal.present();

        modal.onDidDismiss().then(async (data) => {
            if (data.data) {
                this.selectedCode = data.data;
            }
        });
    }

    onChange(e) {
        const val = e.target?.value;

        if (val && val.length > 1) {
            const pastedTextArray = val.split('');
            this.enterPastedCode(pastedTextArray);
        }
    }

    enterPastedCode(pastedTextArray): void {
        let codeInputIndex = 0;

        pastedTextArray.forEach((code) => {
            if (/^\d$/.test(code) && codeInputIndex < this.inputs.length) {
                this.enteredCode[codeInputIndex] = code;
                this.moveToInput(codeInputIndex + 1);
                codeInputIndex++;
            } else if (codeInputIndex == this.inputs.length) {
                return true;
            }
        });
    }

    onKeyDown(event: KeyboardEvent, index: number): void {
        this.errorType = null;

        if (event.key === 'Backspace') {
            this.enteredCode[index] = '';
            this.moveToInput(index - 1);
            event.preventDefault();
            event.stopPropagation();
        } else if (/^\d$/.test(event.key)) {
            this.enteredCode[index] = event.key;
            this.moveToInput(index + 1);
            event.preventDefault();
            event.stopPropagation();
        }
    }

    onPaste(event: ClipboardEvent): void {
        const clipboardData = event.clipboardData;
        const pastedText = clipboardData.getData('text');
        const pastedTextArray = pastedText.split('');
        event.preventDefault();
        event.stopPropagation();

        this.enterPastedCode(pastedTextArray);
    }

    moveToInput(index): void {
        if (index >= 0 && index < this.inputs.toArray().length) {
            this.focusInput(index);
        }
    }

    focusInput(index): void {
        this.inputs.toArray()[index].setFocus();
    }
}
